/**
 * Heatmap Component
 *
 * Hand-rolled SVG `<rect>` grid (no extra dependency). Reads
 * `payload.heatmap.{x_categories, y_categories, cells}` from the resolver
 * and shades each cell from light to dark blue based on its relative value.
 */

import { useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { useIndicatorData } from './useIndicatorData';

interface HeatmapComponentProps {
  indicatorId?: number;
}

const CELL_W = 64;
const CELL_H = 36;
const Y_LABEL_W = 110;
const X_LABEL_H = 60;
const PAD = 8;

function shadeFor(value: number, min: number, max: number): string {
  if (max === min) return 'rgb(191, 219, 254)';
  const ratio = (value - min) / (max - min);
  // light blue (191,219,254) → deep blue (29,78,216)
  const r = Math.round(191 + (29 - 191) * ratio);
  const g = Math.round(219 + (78 - 219) * ratio);
  const b = Math.round(254 + (216 - 254) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function textColorFor(ratio: number): string {
  return ratio > 0.55 ? '#ffffff' : '#0f172a';
}

export function HeatmapComponent({ indicatorId }: HeatmapComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);

  const heatmap = live?.heatmap;
  const xCats = heatmap?.x_categories || [];
  const yCats = heatmap?.y_categories || [];
  const cells = heatmap?.cells || [];

  const lookup = useMemo(() => {
    const map: Record<string, number> = {};
    cells.forEach((c) => { map[`${c.x}|${c.y}`] = c.value; });
    return map;
  }, [cells]);

  const { min, max } = useMemo(() => {
    if (cells.length === 0) return { min: 0, max: 0 };
    let mi = Number.POSITIVE_INFINITY;
    let ma = Number.NEGATIVE_INFINITY;
    cells.forEach((c) => {
      if (c.value < mi) mi = c.value;
      if (c.value > ma) ma = c.value;
    });
    return { min: mi, max: ma };
  }, [cells]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (xCats.length === 0 || yCats.length === 0) {
    return (
      <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">
        No heatmap data.
      </div>
    );
  }

  const width = Y_LABEL_W + xCats.length * CELL_W + PAD * 2;
  const height = X_LABEL_H + yCats.length * CELL_H + PAD * 2;

  return (
    <Card>
      <CardContent className="p-3 overflow-auto">
        <svg width={width} height={height} role="img" aria-label="Heatmap">
          {/* X-axis labels (rotated) */}
          {xCats.map((label, i) => {
            const cx = Y_LABEL_W + i * CELL_W + CELL_W / 2 + PAD;
            return (
              <text
                key={`x-${label}`}
                x={cx}
                y={X_LABEL_H + PAD - 6}
                textAnchor="end"
                fontSize={11}
                transform={`rotate(-35, ${cx}, ${X_LABEL_H + PAD - 6})`}
                className="fill-muted-foreground"
              >
                {label}
              </text>
            );
          })}
          {/* Y-axis labels + cells */}
          {yCats.map((yLabel, yi) => (
            <g key={`row-${yLabel}`}>
              <text
                x={Y_LABEL_W + PAD - 6}
                y={X_LABEL_H + yi * CELL_H + CELL_H / 2 + PAD + 4}
                textAnchor="end"
                fontSize={11}
                className="fill-muted-foreground"
              >
                {yLabel}
              </text>
              {xCats.map((xLabel, xi) => {
                const value = lookup[`${xLabel}|${yLabel}`] ?? 0;
                const ratio = max === min ? 0 : (value - min) / (max - min);
                const fill = shadeFor(value, min, max);
                const tx = Y_LABEL_W + xi * CELL_W + PAD;
                const ty = X_LABEL_H + yi * CELL_H + PAD;
                return (
                  <g key={`${xLabel}-${yLabel}`}>
                    <rect
                      x={tx + 1}
                      y={ty + 1}
                      width={CELL_W - 2}
                      height={CELL_H - 2}
                      rx={3}
                      fill={fill}
                    />
                    <text
                      x={tx + CELL_W / 2}
                      y={ty + CELL_H / 2 + 4}
                      textAnchor="middle"
                      fontSize={11}
                      fill={textColorFor(ratio)}
                    >
                      {value}
                    </text>
                  </g>
                );
              })}
            </g>
          ))}
        </svg>
      </CardContent>
    </Card>
  );
}
